'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'

import { useForm, type SubmitHandler } from 'react-hook-form'

import { z } from 'zod'

import { Deezer, Google, Spotify, YoutubeMusic } from '@/app/components/Icons'
import Button from '@/app/components/ui/Button'
import CustomCheckbox from '@/app/components/ui/Checkbox'
import Input from '@/app/components/ui/Input'
import Typography from '@/app/components/ui/Typograph'
import styles from '@/app/styles/pages/sign-up.module.scss'
import { Separator } from '@/components/ui/separator'

const registerSchema = z
  .object({
    email: z.string().min(1, 'Campo obrigatório').email('Deve ser um email válido'),
    password: z.string().min(1, 'Campo obrigatório'),
    confirmPassword: z.string().min(1, 'Campo obrigatório'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'As senhas devem ser iguais',
    path: ['confirmPassword'],
  })

type LoginFormInputs = z.infer<typeof registerSchema>

export default function SignIn() {
  const [rememberMe, setRememberMe] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormInputs>({
    resolver: zodResolver(registerSchema),
  })

  const onSubmit: SubmitHandler<LoginFormInputs> = async data => {
    console.log(data)
  }

  return (
    <div className={styles.signUpContainer}>
      <div className="flex flex-col flex-1 pt-10">
        <div className={styles.logo}>
          <Image
            src="/assets/logos/logo-branco-simples.svg"
            alt="Logo"
            width={100}
            height={100}
            priority
          />
        </div>

        <div className={styles.signUpForm}>
          <h1 className={styles.title}>Criar uma conta</h1>

          <Link href="/sign-up/distribution" className="mt-5 flex items-center justify-between rounded-xl border border-emerald-400/20 bg-emerald-400/[.06] px-4 py-3 text-sm text-emerald-100 transition hover:bg-emerald-400/[.1]">
            <span><b className="block">Conta para distribuição</b><small className="text-xs text-emerald-200/50">Selo, distribuidora ou management</small></span>
            <span aria-hidden>→</span>
          </Link>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
            <Input
              label="Email"
              id="email"
              placeholder="Endereço de e-mail"
              variant="outlined"
              {...register('email')}
              error={errors.email?.message}
              className="mb-4"
            />
            <Input
              label="Senha"
              type="password"
              id="password"
              placeholder="Senha"
              variant="outlined"
              {...register('password')}
              error={errors.password?.message}
              className="mb-4"
            />
            <Input
              label="Confirmar Senha"
              type="password"
              id="confirmPassword"
              placeholder="Confirmar Senha"
              variant="outlined"
              {...register('confirmPassword')}
              error={errors.confirmPassword?.message}
            />
            <CustomCheckbox checked={rememberMe} onCheckedChange={setRememberMe} className="mt-5">
              Concordo com os{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--primary)] leading-5"
              >
                Termos de Serviço
              </a>{' '}
              e confirmo que li a{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--primary)] leading-5"
              >
                Política de Privacidade
              </a>{' '}
              assim como confirmo ter mais de 18 anos.
            </CustomCheckbox>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
              className="mt-6 w-full"
            >
              <span>{isSubmitting ? 'Carregando..' : 'Crie uma conta'}</span>
            </Button>
          </form>

          <div className="relative my-6">
            <Separator className="bg-[var(--background-300)]" />
            <Typography className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#09080b] px-3 text-neutral-400">
              ou entre com
            </Typography>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <Button type="button" variant="outlined" className="w-full">
              <Google />
            </Button>
            <Button type="button" variant="outlined" className="w-full">
              <Spotify />
            </Button>
            <Button type="button" variant="outlined" className="w-full">
              <YoutubeMusic />
            </Button>
            <Button type="button" variant="outlined" className="w-full">
              <Deezer />
            </Button>
          </div>

          <div className={styles.signIn}>
            Já tem uma conta?
            <Link href="/sign-in">
              <Button type="button" variant="simple" className="ml-2">
                <span>Faça login</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
